import type { StoreBranding } from '@avrahami/contracts';
import { Alert, Button, Loading, PageHeader } from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import { useCan } from '../api/session';
import { brandingQuery, keys, tenantApi } from '../api/tenant';
import { FormStatus, ImageUploadField, mergeErrors } from '../components/fields';
import { StaleNotice, useVersionedDraft } from '../components/versioned';
import { he } from '../i18n/he';
import { brandingDraft, brandingRequest } from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';

export function BrandingPage() {
  const branding = useQuery(brandingQuery());
  return (
    <>
      <PageHeader title={he.branding.title} />
      {branding.isPending ? <Loading /> : null}
      {branding.isError ? <Alert>{errorMessage(branding.error)}</Alert> : null}
      {branding.data === undefined ? null : <BrandingForm branding={branding.data} />}
    </>
  );
}

function BrandingForm({ branding }: { branding: StoreBranding }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('store.settings.write');
  const editor = useVersionedDraft(branding, (value) => value.version, brandingDraft);
  const { draft, set } = editor;
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const save = useMutation({
    mutationFn: tenantApi.updateBranding,
    onSuccess: (updated) => {
      editor.reset(updated);
      queryClient.setQueryData(keys.branding(), updated);
    },
  });
  const reload = () => {
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...brandingQuery(), staleTime: 0 })
      // A failed read shows on the page itself (the branding query's error).
      .then(editor.reset, () => undefined);
  };

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    const parsed = brandingRequest(draft, editor.baseVersion);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return;
    }
    setLocalErrors(NO_ERRORS);
    save.mutate(parsed.value);
  };

  const errors = mergeErrors(localErrors, save.error);

  return (
    <form onSubmit={onSubmit} noValidate data-testid="branding-form">
      <div className="flex flex-col gap-6">
        {canWrite ? null : <Alert tone="info">{he.common.readOnly}</Alert>}
        <StaleNotice stale={editor.stale} onReload={reload} />

        <div className="grid gap-6 lg:grid-cols-2">
          <ImageUploadField
            label={he.branding.logo}
            value={draft.logo}
            disabled={!canWrite}
            error={errors['logoMediaId']}
            testId="logo-upload"
            onChange={(logo) => {
              set({ logo });
            }}
          />
          <ImageUploadField
            label={he.branding.favicon}
            hint={he.branding.faviconHint}
            value={draft.favicon}
            disabled={!canWrite}
            error={errors['faviconMediaId']}
            testId="favicon-upload"
            onChange={(favicon) => {
              set({ favicon });
            }}
          />
        </div>

        <FormStatus
          error={save.error}
          localInvalid={Object.keys(localErrors).length > 0}
          saved={save.isSuccess}
          onReload={reload}
        />
        {canWrite ? (
          <div>
            <Button type="submit" busy={save.isPending}>
              {he.common.save}
            </Button>
          </div>
        ) : null}
      </div>
    </form>
  );
}
